import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import {
  X,
  AlertCircle,
  ExternalLink,
  ChevronDown,
  Check,
  Sparkles,
  User,
  Phone,
  Mail,
  Calendar,
  IndianRupee,
  MapPin,
  Tag,
  FileText,
  Loader2,
  UserPlus,
  Pencil,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  createLeadAPI,
  updateLeadAPI,
  getWhatsAppIntakeAPI,
  listLeadsAPI,
} from "../../../../Redux/ApiCalls/Dashboard/LeadsAPI";
import { showSuccessToast, showErrorToast } from "../../../atoms/Utlis/Toast";
import BrandDatePicker from "../../../atoms/Utlis/BrandDatePicker";
import type {
  CreateLeadPayload,
  LeadEventType,
  LeadSource,
  WhatsAppIntakeResponse,
  LeadDetail,
} from "../../../../Data/leads.dto";
import { EVENT_TYPES, SOURCES } from "../../../../Data/leads.dto";
import { countryCodes } from "../../../../Utils/countryCodes";
import {
  ModalOverlay,
  ModalShell,
  ModalHero,
  HeroGlow,
  HeroRow,
  HeroText,
  HeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
  FormCard,
} from "../../../molecules/Dashboard/EventCalendar/eventModalSharedStyles";
import {
  ModalActions,
  SecondaryButton,
  PrimaryButton,
} from "../quotationMasterListShared";
import {
  Field,
  FieldGrid,
  FieldFull,
  Label,
  Input,
  ErrorSpan,
  FormLabelIcon,
  FormGradientDefs,
  RequiredStar,
} from "../../../molecules/Dashboard/Member/memberFormStyles";

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 0.8s linear infinite;
`;

const StyledInput = styled(Input) <{ $hasError?: boolean; $highlight?: boolean }>`
  border-color: ${(p) =>
    p.$hasError ? "#e44862" : p.$highlight ? "#f59e0b" : "rgba(23, 23, 23, 0.1)"};
  background: ${(p) => (p.$highlight ? "#fffbeb" : "#fafafa")};

  &:focus {
    border-color: ${(p) =>
    p.$hasError ? "#e44862" : p.$highlight ? "#f59e0b" : "rgba(201, 77, 170, 0.4)"};
  }
`;

const DateHighlightWrap = styled.div<{ $highlight?: boolean }>`
  ${(p) =>
    p.$highlight
      ? `
    .MuiFormControl-root {
      background: #fffbeb;
    }
    .MuiOutlinedInput-notchedOutline {
      border-color: #f59e0b !important;
    }
  `
      : ""}
`;

const TextArea = styled.textarea<{ $hasError?: boolean; $highlight?: boolean }>`
  width: 100%;
  min-height: 88px;
  box-sizing: border-box;
  background: ${(p) => (p.$highlight ? "#fffbeb" : "#fafafa")};
  border: 1px solid
    ${(p) =>
    p.$hasError ? "#e44862" : p.$highlight ? "#f59e0b" : "rgba(23, 23, 23, 0.1)"};
  border-radius: 12px;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 600;
  padding: 12px 14px;
  color: #171717;
  resize: vertical;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;

  &:focus {
    outline: none;
    border-color: rgba(201, 77, 170, 0.4);
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.1);
    background: #fff;
  }

  &::placeholder {
    color: rgba(23, 23, 23, 0.35);
    font-weight: 500;
  }
`;

const SelectWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const SelectTrigger = styled.div<{
  $isOpen: boolean;
  $hasError?: boolean;
  $highlight?: boolean;
  $hasValue: boolean;
}>`
  width: 100%;
  height: 48px;
  padding: 0 16px;
  border: 1px solid
    ${(p) =>
    p.$hasError ? "#e44862" : p.$isOpen ? "rgba(201, 77, 170, 0.4)" : p.$highlight ? "#f59e0b" : "rgba(23, 23, 23, 0.1)"};
  border-radius: 12px;
  font-family: Urbanist, sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: ${(p) => (p.$hasValue ? "#171717" : "rgba(23, 23, 23, 0.35)")};
  outline: none;
  box-sizing: border-box;
  background: ${(p) => (p.$highlight ? "#fffbeb" : "#fafafa")};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  user-select: none;
  transition: all 0.15s ease;
  box-shadow: ${(p) => (p.$isOpen ? "0 0 0 3px rgba(201, 77, 170, 0.1)" : "none")};

  &:hover {
    border-color: ${(p) => (p.$hasError ? "#e44862" : "rgba(201, 77, 170, 0.35)")};
  }
`;

const SelectMenu = styled.div<{ $top: number; $left: number; $width: number }>`
  position: fixed;
  top: ${(p) => p.$top}px;
  left: ${(p) => p.$left}px;
  width: ${(p) => p.$width}px;
  max-height: 240px;
  background: #fff;
  border: 1px solid rgba(201, 77, 170, 0.18);
  border-radius: 14px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.12);
  z-index: 10050;
  overflow-y: auto;
  padding: 6px;
  box-sizing: border-box;
`;

const SelectItem = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-radius: 10px;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: ${(p) => (p.$isSelected ? 700 : 600)};
  color: ${(p) => (p.$isSelected ? "#c94daa" : "#171717")};
  background: ${(p) => (p.$isSelected ? "rgba(201, 77, 170, 0.08)" : "transparent")};
  cursor: pointer;
  transition: all 0.15s ease;
  margin-bottom: 2px;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    background: rgba(201, 77, 170, 0.06);
    color: #c94daa;
  }
`;

interface CustomSelectProps {
  id?: string;
  value: string;
  onChange: (val: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  hasError?: boolean;
  highlight?: boolean;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  id,
  value,
  onChange,
  options,
  placeholder = "Select",
  hasError,
  highlight,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const selectedOption = options.find((o) => o.value === value);

  const updateMenuPosition = () => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const menuHeight = Math.min(240, options.length * 44 + 12);
    const spaceBelow = window.innerHeight - rect.bottom - 12;
    const openUpward = spaceBelow < menuHeight && rect.top > spaceBelow;

    setMenuPos({
      top: openUpward ? rect.top - menuHeight - 6 : rect.bottom + 6,
      left: rect.left,
      width: rect.width,
    });
  };

  useEffect(() => {
    if (!isOpen) return;

    updateMenuPosition();

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
    };

    const handleReposition = () => updateMenuPosition();

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [isOpen, options.length]);

  return (
    <SelectWrapper ref={containerRef} id={id}>
      <SelectTrigger
        $isOpen={isOpen}
        $hasError={hasError}
        $highlight={highlight}
        $hasValue={!!selectedOption && selectedOption.value !== ""}
        onClick={() => {
          if (!isOpen) updateMenuPosition();
          setIsOpen(!isOpen);
        }}
      >
        <span>
          {selectedOption && selectedOption.value !== "" ? selectedOption.label : placeholder}
        </span>
        <ChevronDown
          size={18}
          color={isOpen ? "#c94daa" : "rgba(23,23,23,0.45)"}
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
            flexShrink: 0,
            marginLeft: 8,
          }}
        />
      </SelectTrigger>

      {isOpen &&
        createPortal(
          <SelectMenu
            ref={menuRef}
            $top={menuPos.top}
            $left={menuPos.left}
            $width={menuPos.width}
          >
            {options.map((opt) => {
              const isSelected = opt.value === value;
              return (
                <SelectItem
                  key={opt.value}
                  $isSelected={isSelected}
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                  }}
                >
                  <span>{opt.label}</span>
                  {isSelected && <Check size={16} color="#c94daa" style={{ flexShrink: 0 }} />}
                </SelectItem>
              );
            })}
          </SelectMenu>,
          document.body
        )}
    </SelectWrapper>
  );
};

const ViewLeadLink = styled.span`
  color: #c94daa;
  font-weight: 700;
  cursor: pointer;
  text-decoration: underline;
  margin-left: 4px;
`;

const Banner = styled.div<{ $variant: "warning" | "blocking" | "info" }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 14px;
  background: ${(p) =>
    p.$variant === "blocking"
      ? "rgba(228, 72, 98, 0.08)"
      : p.$variant === "warning"
        ? "rgba(240, 154, 62, 0.1)"
        : "rgba(53, 183, 200, 0.08)"};
  border: 1px solid
    ${(p) =>
    p.$variant === "blocking"
      ? "rgba(228, 72, 98, 0.2)"
      : p.$variant === "warning"
        ? "rgba(240, 154, 62, 0.25)"
        : "rgba(53, 183, 200, 0.2)"};
`;

const BannerText = styled.p`
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.78);
  margin: 0;
  line-height: 1.5;
`;

const LoadingCenter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 40px 0;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.55);
`;

const PhoneRow = styled.div<{ $hasError?: boolean; $highlight?: boolean }>`
  display: flex;
  align-items: stretch;
  gap: 8px;
`;

const CountryCodeWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  position: relative;
  cursor: pointer;
  user-select: none;
  background: #fafafa;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  padding: 0 14px;
  height: 48px;
  flex-shrink: 0;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: rgba(201, 77, 170, 0.3);
  }
`;

const CountryCodeLabel = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #171717;
  font-family: Urbanist, sans-serif;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

const DropdownContainer = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: 280px;
  max-height: 250px;
  background: #fff;
  border: 1px solid rgba(23, 23, 23, 0.08);
  border-radius: 14px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.12);
  z-index: 300;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SearchInputWrapper = styled.div`
  padding: 10px;
  border-bottom: 1px solid rgba(23, 23, 23, 0.06);
  background: #fff;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 10px;
  font-size: 13px;
  font-family: Urbanist, sans-serif;
  font-weight: 600;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: rgba(201, 77, 170, 0.4);
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.1);
  }
`;

const CountryList = styled.div`
  overflow-y: auto;
  flex: 1;
`;

const CountryListItem = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  padding: 10px 12px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  background: ${(p) => (p.$active ? "rgba(201, 77, 170, 0.08)" : "transparent")};

  &:hover {
    background: rgba(201, 77, 170, 0.06);
  }
`;

const CountryFlag = styled.span`
  font-size: 16px;
  margin-right: 8px;
`;

const CountryName = styled.span`
  font-size: 13px;
  font-family: Urbanist, sans-serif;
  font-weight: 600;
  color: #171717;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CountryDialCode = styled.span`
  font-size: 13px;
  font-family: Urbanist, sans-serif;
  font-weight: 700;
  color: #c94daa;
  margin-left: 8px;
`;

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess?: (leadId: string) => void;
  customerNumber?: string;
  leadToEdit?: LeadDetail | null;
}

const AddLeadModal: React.FC<Props> = ({
  open,
  onClose,
  onSuccess,
  customerNumber,
  leadToEdit,
}) => {
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [eventType, setEventType] = useState<LeadEventType | "">("");
  const [customEventType, setCustomEventType] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [budgetMin, setBudgetMin] = useState("");
  const [budgetMax, setBudgetMax] = useState("");
  const [city, setCity] = useState("");
  const [source, setSource] = useState<LeadSource>("whatsapp");
  const [notes, setNotes] = useState("");

  const [nameErr, setNameErr] = useState("");
  const [phoneErr, setPhoneErr] = useState("");
  const [eventTypeErr, setEventTypeErr] = useState("");
  const [customEventTypeErr, setCustomEventTypeErr] = useState("");
  const [emailErr, setEmailErr] = useState("");

  const [intakeLoading, setIntakeLoading] = useState(false);
  const [intake, setIntake] = useState<WhatsAppIntakeResponse | null>(null);
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const [countryCode, setCountryCode] = useState("+91");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open) return;
    if (leadToEdit) {
      setName(leadToEdit.name || "");
      setEmail(leadToEdit.email || "");
      setEventType(leadToEdit.event_type || "");
      setEventDate(leadToEdit.event_date ? leadToEdit.event_date.split("T")[0] : "");
      setBudgetMin(leadToEdit.budget_min || "");
      setBudgetMax(leadToEdit.budget_max || "");
      setCity(leadToEdit.venue_city || "");
      setSource(leadToEdit.source || "whatsapp");

      let rawNotes = leadToEdit.notes || "";
      if (leadToEdit.event_type === "other" && rawNotes) {
        const match = rawNotes.match(/^Event Type Details:\s*([^\n]+)/);
        if (match) {
          setCustomEventType(match[1].trim());
          rawNotes = rawNotes.replace(/^Event Type Details:\s*[^\n]+\n?\n?/, "");
        }
      }
      setNotes(rawNotes);
      setPhone(leadToEdit.phone || "");
      if (leadToEdit.country_code) {
        setCountryCode(leadToEdit.country_code);
      }
    }
  }, [open, leadToEdit]);

  useEffect(() => {
    if (!open || !customerNumber || leadToEdit) return;
    setIntakeLoading(true);
    getWhatsAppIntakeAPI(customerNumber)
      .then((data) => {
        setIntake(data);
        setMissingFields(data.missing_fields || []);
        const c = data.collected_data || {};
        if (c.name) setName(c.name);
        setPhone(c.phone || "");
        if (c.country_code) setCountryCode(c.country_code);
        if (customerNumber.startsWith("91")) setSource("whatsapp");
      })
      .catch(() => { })
      .finally(() => setIntakeLoading(false));
  }, [open, customerNumber, leadToEdit]);

  const handleClose = () => {
    setName("");
    setPhone("");
    setEmail("");
    setEventType("");
    setCustomEventType("");
    setEventDate("");
    setBudgetMin("");
    setBudgetMax("");
    setCity("");
    setNotes("");
    setNameErr("");
    setPhoneErr("");
    setEventTypeErr("");
    setCustomEventTypeErr("");
    setEmailErr("");
    setIntake(null);
    setMissingFields([]);
    setCountryCode("+91");
    onClose();
  };

  const validate = (): boolean => {
    let valid = true;
    if (!name.trim()) {
      setNameErr("Name is required");
      valid = false;
    } else setNameErr("");
    if (!phone.trim()) {
      setPhoneErr("Phone number is required");
      valid = false;
    } else if (phone.trim().length < 10 || phone.trim().length > 15) {
      setPhoneErr("Phone number must be between 10 and 15 digits");
      valid = false;
    } else setPhoneErr("");
    if (!eventType) {
      setEventTypeErr("Event type is required");
      valid = false;
    } else {
      setEventTypeErr("");
      if (eventType === "other" && !customEventType.trim()) {
        setCustomEventTypeErr("Please specify the event type");
        valid = false;
      } else {
        setCustomEventTypeErr("");
      }
    }

    if (email.trim() !== "") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        setEmailErr("Please enter a valid email address");
        valid = false;
      } else {
        setEmailErr("");
      }
    } else {
      setEmailErr("");
    }
    return valid;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    const cleanPhone = phone.replace(/[^\d]/g, "");

    let finalNotes = notes.trim();
    if (eventType === "other" && customEventType.trim()) {
      const customDetail = `Event Type Details: ${customEventType.trim()}`;
      finalNotes = finalNotes ? `${customDetail}\n\n${finalNotes}` : customDetail;
    }

    const payload: CreateLeadPayload = {
      name: name.trim(),
      phone: cleanPhone,
      country_code: countryCode,
      event_type: eventType as LeadEventType,
      email: email || undefined,
      event_date: eventDate || undefined,
      venue_city: city || undefined,
      budget_min: budgetMin !== "" ? budgetMin : undefined,
      budget_max: budgetMax !== "" ? budgetMax : undefined,
      source,
      notes: finalNotes || undefined,
      ...(customerNumber && { source_ref: customerNumber }),
    };

    try {
      if (leadToEdit) {
        await updateLeadAPI(leadToEdit.id, payload);
        showSuccessToast("Lead updated successfully!");
        onSuccess?.(leadToEdit.id);
        handleClose();
      } else {
        const lead = await createLeadAPI(payload);
        showSuccessToast("Lead created successfully!");
        onSuccess?.(lead.id);
        handleClose();
      }
    } catch (err: any) {
      const data = err?.response?.data;
      if (data?.non_field_errors?.length) {
        const msg: string = data.non_field_errors[0];
        if (msg.includes("phone number is already active")) {
          setPhoneErr("This phone number already has an active lead.");
        } else {
          showErrorToast(msg);
        }
      } else if (data?.phone) {
        setPhoneErr(data.phone[0]);
      } else if (data?.email) {
        setEmailErr(data.email[0]);
        showErrorToast(data.email[0]);
      } else if (data && typeof data === "object") {
        const firstKey = Object.keys(data)[0];
        const val = data[firstKey];
        if (Array.isArray(val) && val.length > 0) {
          showErrorToast(val[0]);
        } else if (typeof val === "string") {
          showErrorToast(val);
        } else {
          showErrorToast(`Failed to ${leadToEdit ? "update" : "create"} lead. Please try again.`);
        }
      } else {
        showErrorToast(`Failed to ${leadToEdit ? "update" : "create"} lead. Please try again.`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isHighlighted = (field: string) => missingFields.includes(field);

  const isEdit = !!leadToEdit;
  const heroTitle = isEdit ? "Edit Lead" : "Add Lead";
  const heroSubtitle = customerNumber
    ? "Complete lead details from the WhatsApp conversation."
    : isEdit
      ? "Update lead information and save your changes."
      : "Capture a new lead with contact and event details.";
  const heroBadge = customerNumber ? "WhatsApp intake" : isEdit ? "Lead update" : "New lead";

  const renderShell = (
    body: React.ReactNode,
    footer?: React.ReactNode,
    options?: { disableOverlayClose?: boolean }
  ) =>
    createPortal(
      <>
        <FormGradientDefs />
        <ModalOverlay
          onClick={(e) => {
            if (e.target === e.currentTarget && !options?.disableOverlayClose) handleClose();
          }}
        >
          <ModalShell style={{ maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
            <ModalHero>
              <HeroGlow />
              <HeroRow>
                <HeroText>
                  <HeroBadge>
                    <Sparkles size={11} />
                    {heroBadge}
                  </HeroBadge>
                  <ModalTitle>{heroTitle}</ModalTitle>
                  <ModalSubtitle>{heroSubtitle}</ModalSubtitle>
                </HeroText>
                <CloseBtn type="button" onClick={handleClose} aria-label="Close">
                  <X size={18} />
                </CloseBtn>
              </HeroRow>
            </ModalHero>
            <ModalBody>{body}</ModalBody>
            {footer}
          </ModalShell>
        </ModalOverlay>
      </>,
      document.body
    );

  if (!open) return null;

  if (customerNumber && intake?.active_lead_id) {
    return renderShell(
      <FormCard>
        <Banner $variant="blocking">
          <AlertCircle size={18} color="#e44862" style={{ flexShrink: 0, marginTop: 2 }} />
          <BannerText>
            This customer already has an active lead.
            <ViewLeadLink
              onClick={() => {
                handleClose();
                navigate(`/dashboard/leads/${intake.active_lead_id}`);
              }}
            >
              View Lead <ExternalLink size={12} style={{ display: "inline", verticalAlign: "middle" }} />
            </ViewLeadLink>
          </BannerText>
        </Banner>
      </FormCard>,
      (
        <ModalActions>
          <SecondaryButton type="button" onClick={handleClose}>
            Close
          </SecondaryButton>
        </ModalActions>
      )
    );
  }

  if (customerNumber && intakeLoading) {
    return renderShell(
      <LoadingCenter>
        <Spinner size={28} color="#c94daa" />
        Checking WhatsApp conversation…
      </LoadingCenter>,
      undefined,
      { disableOverlayClose: true }
    );
  }

  return renderShell(
    <>
      {customerNumber && intake && !intake.can_create_lead && (
        <Banner $variant="warning">
          <AlertCircle size={18} color="#f09a3e" style={{ flexShrink: 0, marginTop: 2 }} />
          <BannerText>
            Intake incomplete. Fill highlighted fields manually to create lead.
          </BannerText>
        </Banner>
      )}

      <FormCard>
        <FieldGrid>
          <Field>
            <Label htmlFor="lead-name">
              <FormLabelIcon icon={User} />
              Name
              <RequiredStar>*</RequiredStar>
            </Label>
            <StyledInput
              id="lead-name"
              placeholder="Full name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setNameErr("");
              }}
              $hasError={!!nameErr}
              $highlight={isHighlighted("name")}
            />
            {nameErr && <ErrorSpan>{nameErr}</ErrorSpan>}
          </Field>

          <Field>
            <Label htmlFor="lead-phone">
              <FormLabelIcon icon={Phone} />
              Phone
              <RequiredStar>*</RequiredStar>
            </Label>
            <PhoneRow $hasError={!!phoneErr} $highlight={isHighlighted("phone")}>
              <CountryCodeWrapper
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                ref={dropdownRef}
              >
                <CountryCodeLabel>
                  {countryCode} <ChevronDown size={14} />
                </CountryCodeLabel>
                {isDropdownOpen && (
                  <DropdownContainer onClick={(e) => e.stopPropagation()}>
                    <SearchInputWrapper>
                      <SearchInput
                        type="text"
                        placeholder="Search country..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                      />
                    </SearchInputWrapper>
                    <CountryList>
                      {countryCodes
                        .filter(
                          (c) =>
                            c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            c.code.includes(searchQuery)
                        )
                        .map((c) => (
                          <CountryListItem
                            key={`${c.name}-${c.code}`}
                            $active={countryCode === c.code}
                            onClick={(e) => {
                              e.stopPropagation();
                              setCountryCode(c.code);
                              setIsDropdownOpen(false);
                              setSearchQuery("");
                            }}
                          >
                            <CountryFlag>{c.flag}</CountryFlag>
                            <CountryName>{c.name}</CountryName>
                            <CountryDialCode>{c.code}</CountryDialCode>
                          </CountryListItem>
                        ))}
                    </CountryList>
                  </DropdownContainer>
                )}
              </CountryCodeWrapper>
              <StyledInput
                id="lead-phone"
                placeholder="Enter phone number"
                value={phone}
                maxLength={15}
                onChange={(e) => {
                  setPhone(e.target.value.replace(/\D/g, ""));
                  setPhoneErr("");
                }}
                $hasError={!!phoneErr}
                $highlight={isHighlighted("phone")}
                style={{ flex: 1 }}
              />
            </PhoneRow>
            {phoneErr && (
              <ErrorSpan>
                {phoneErr}
                {phoneErr.includes("already active") && (
                  <ViewLeadLink
                    onClick={async () => {
                      try {
                        const res = await listLeadsAPI({ search: phone });
                        if (res.results?.[0]) {
                          handleClose();
                          navigate(`/dashboard/leads/${res.results[0].id}`);
                        }
                      } catch { }
                    }}
                  >
                    {" "}
                    View Lead →
                  </ViewLeadLink>
                )}
              </ErrorSpan>
            )}
          </Field>

          <Field>
            <Label htmlFor="lead-email">
              <FormLabelIcon icon={Mail} />
              Email
            </Label>
            <StyledInput
              id="lead-email"
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailErr("");
              }}
              $hasError={!!emailErr}
            />
            {emailErr && <ErrorSpan>{emailErr}</ErrorSpan>}
          </Field>

          <Field>
            <Label htmlFor="lead-event-type">
              <FormLabelIcon icon={Calendar} />
              Event Type
              <RequiredStar>*</RequiredStar>
            </Label>
            <CustomSelect
              id="lead-event-type"
              value={eventType}
              onChange={(val) => {
                setEventType(val as LeadEventType);
                setEventTypeErr("");
                if (val !== "other") {
                  setCustomEventType("");
                  setCustomEventTypeErr("");
                }
              }}
              options={[{ value: "", label: "Select" }, ...EVENT_TYPES]}
              placeholder="Select event type"
              hasError={!!eventTypeErr}
              highlight={isHighlighted("event_type")}
            />
            {eventTypeErr && <ErrorSpan>{eventTypeErr}</ErrorSpan>}
          </Field>

          {eventType === "other" && (
            <Field>
              <Label htmlFor="lead-custom-event-type">
                <FormLabelIcon icon={Tag} />
                Specify Event Type
                <RequiredStar>*</RequiredStar>
              </Label>
              <StyledInput
                id="lead-custom-event-type"
                placeholder="e.g. Baby Shower, Housewarming"
                value={customEventType}
                onChange={(e) => {
                  setCustomEventType(e.target.value);
                  setCustomEventTypeErr("");
                }}
                $hasError={!!customEventTypeErr}
              />
              {customEventTypeErr && <ErrorSpan>{customEventTypeErr}</ErrorSpan>}
            </Field>
          )}

          <Field>
            <Label htmlFor="lead-event-date">
              <FormLabelIcon icon={Calendar} />
              Event Date
            </Label>
            <DateHighlightWrap $highlight={isHighlighted("event_date")}>
              <BrandDatePicker
                name="lead-event-date"
                value={eventDate}
                onChange={(v) => setEventDate(v)}
              />
            </DateHighlightWrap>
          </Field>

          <Field>
            <Label htmlFor="lead-budget">
              <FormLabelIcon icon={IndianRupee} />
              Budget (₹)
            </Label>
            <StyledInput
              id="lead-budget"
              type="number"
              placeholder="50000"
              value={budgetMin}
              onChange={(e) => {
                const val = e.target.value;
                let sanitized = val;
                if (val !== "") {
                  const parsed = parseFloat(val);
                  sanitized = isNaN(parsed) ? "" : String(Math.max(0, parsed));
                }
                setBudgetMin(sanitized);
                setBudgetMax(sanitized);
              }}
            />
          </Field>

          <Field>
            <Label htmlFor="lead-city">
              <FormLabelIcon icon={MapPin} />
              City
            </Label>
            <StyledInput
              id="lead-city"
              placeholder="e.g. Ahmedabad"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              $highlight={isHighlighted("venue_city")}
            />
          </Field>

          <Field>
            <Label htmlFor="lead-source">
              <FormLabelIcon icon={Tag} />
              Source
            </Label>
            <CustomSelect
              id="lead-source"
              value={source}
              onChange={(val) => setSource(val as LeadSource)}
              options={SOURCES}
            />
          </Field>

          <FieldFull>
            <Label htmlFor="lead-notes">
              <FormLabelIcon icon={FileText} />
              Notes
            </Label>
            <TextArea
              id="lead-notes"
              placeholder="Additional notes about this lead"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </FieldFull>
        </FieldGrid>
      </FormCard>
    </>,
    (
      <ModalActions>
        <SecondaryButton type="button" onClick={handleClose} disabled={submitting}>
          Cancel
        </SecondaryButton>
        <PrimaryButton
          id="add-lead-submit-btn"
          type="button"
          disabled={submitting}
          onClick={handleSubmit}
        >
          {submitting ? (
            <>
              <Spinner size={16} />
              {isEdit ? "Saving..." : "Adding..."}
            </>
          ) : (
            <>
              {isEdit ? <Pencil size={16} /> : <UserPlus size={16} />}
              {isEdit ? "Save Changes" : "Add Lead"}
            </>
          )}
        </PrimaryButton>
      </ModalActions>
    )
  );
};

export default AddLeadModal;
